import { forwardRef } from 'react';

import type { IncidentStatus } from '../../../types';
import * as formatters from '../../../utils/formatters';
import { Badge, type BadgeProps } from './Badge';
import type { StatusTone } from './types';

export interface StatusBadgeProps extends Omit<BadgeProps, 'variant' | 'leadingDot' | 'leadingIcon'> {
  status: IncidentStatus;
  showDot?: boolean;
}

type BadgeVariant = NonNullable<BadgeProps['variant']>;

interface IncidentStatusPresentation {
  label: string;
  tone: StatusTone;
  dotColorToken: string;
}

const fallbackPresentations: Record<IncidentStatus, IncidentStatusPresentation> = {
  New: {
    label: 'New',
    tone: 'info',
    dotColorToken: 'var(--color-status-new-solid)',
  },
  InProgress: {
    label: 'In Progress',
    tone: 'warning',
    dotColorToken: 'var(--color-status-in-progress-solid)',
  },
  OnHold: {
    label: 'On Hold',
    tone: 'neutral',
    dotColorToken: 'var(--color-status-on-hold-solid)',
  },
  Resolved: {
    label: 'Resolved',
    tone: 'success',
    dotColorToken: 'var(--color-status-resolved-solid)',
  },
  Closed: {
    label: 'Closed',
    tone: 'neutral',
    dotColorToken: 'var(--color-status-closed-solid)',
  },
  Cancelled: {
    label: 'Cancelled',
    tone: 'neutral',
    dotColorToken: 'var(--color-status-cancelled-solid)',
  },
};

function statusToneToBadgeVariant(tone: StatusTone): BadgeVariant {
  if (tone === 'success') {
    return 'success';
  }

  if (tone === 'warning') {
    return 'warning';
  }

  if (tone === 'info') {
    return 'info';
  }

  return 'neutral';
}

function resolvePresentation(status: IncidentStatus): IncidentStatusPresentation {
  const formatterModule = formatters as unknown as {
    getIncidentStatusPresentation?: (nextStatus: IncidentStatus) => Partial<IncidentStatusPresentation>;
  };

  const derived = formatterModule.getIncidentStatusPresentation?.(status);

  if (derived?.label && derived?.tone && derived.dotColorToken) {
    return {
      label: derived.label,
      tone: derived.tone,
      dotColorToken: derived.dotColorToken,
    };
  }

  return fallbackPresentations[status];
}

function cn(...parts: Array<string | undefined | false | null>): string {
  return parts.filter(Boolean).join(' ');
}

export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(function StatusBadge(
  { status, showDot = true, className, children, ...rest },
  ref,
) {
  const presentation = resolvePresentation(status);
  const variant = statusToneToBadgeVariant(presentation.tone);

  return (
    <Badge
      ref={ref}
      variant={variant}
      className={cn('gap-1.5', className)}
      aria-label={rest['aria-label'] ?? `Status: ${presentation.label}`}
      {...rest}
    >
      {showDot ? (
        <span
          aria-hidden="true"
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: presentation.dotColorToken }}
        />
      ) : null}
      {children ?? presentation.label}
    </Badge>
  );
});
